import { Clock, Monitor, Apple, Smartphone, Tablet, AlertCircle, Mail, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Section = ({
  icon: Icon,
  title,
  steps,
}: {
  icon: React.ElementType;
  title: string;
  steps: React.ReactNode[];
}) => (
  <div className="border border-border rounded-xl p-6 mb-6">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-5 h-5 text-[hsl(var(--brand-teal))]" />
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
    </div>
    <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
      {steps.map((s, i) => (
        <li key={i}>{s}</li>
      ))}
    </ol>
  </div>
);

const ClockHelpPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate('/auth')}
          className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--brand-teal))]/10 flex items-center justify-center">
            <Clock className="w-5 h-5 text-[hsl(var(--brand-teal))]" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Sync Your Device Clock</h1>
        </div>
        <p className="text-muted-foreground mb-10 ml-[52px]">
          LaneRunner requires your device's clock to match real-world time for security. If you see a clock sync message, your device's date or time is incorrect. Most fixes take less than 30 seconds.
        </p>

        <Section
          icon={Monitor}
          title="Windows (most common fix)"
          steps={[
            <>Right-click the clock in your taskbar (bottom-right corner).</>,
            <>Click <span className="text-foreground font-medium">"Adjust date and time"</span>.</>,
            <>Turn <span className="text-foreground font-medium">ON</span> "Set time automatically".</>,
            <>Turn <span className="text-foreground font-medium">ON</span> "Set time zone automatically".</>,
            <>Click <span className="text-foreground font-medium">"Sync now"</span>.</>,
            <>Close this tab, open a new one, and sign in again.</>,
          ]}
        />

        <Section
          icon={Apple}
          title="Mac"
          steps={[
            <>Click the Apple menu → <span className="text-foreground font-medium">System Settings</span>.</>,
            <>Click <span className="text-foreground font-medium">"General"</span> → <span className="text-foreground font-medium">"Date & Time"</span>.</>,
            <>Turn <span className="text-foreground font-medium">ON</span> "Set time and date automatically".</>,
            <>Turn <span className="text-foreground font-medium">ON</span> "Set time zone automatically using your current location".</>,
            <>Close this tab, open a new one, and sign in again.</>,
          ]}
        />

        <Section
          icon={Smartphone}
          title="iPhone / iPad"
          steps={[
            <>Open <span className="text-foreground font-medium">Settings</span>.</>,
            <>Tap <span className="text-foreground font-medium">"General"</span> → <span className="text-foreground font-medium">"Date & Time"</span>.</>,
            <>Turn <span className="text-foreground font-medium">ON</span> "Set Automatically".</>,
            <>Close the LaneRunner app and reopen it.</>,
            <>Sign in again.</>,
          ]}
        />

        <Section
          icon={Tablet}
          title="Android"
          steps={[
            <>Open <span className="text-foreground font-medium">Settings</span>.</>,
            <>Search for <span className="text-foreground font-medium">"Date and time"</span> (location varies by manufacturer).</>,
            <>Turn <span className="text-foreground font-medium">ON</span> "Automatic date & time".</>,
            <>Turn <span className="text-foreground font-medium">ON</span> "Automatic time zone".</>,
            <>Close the LaneRunner app and reopen it.</>,
            <>Sign in again.</>,
          ]}
        />

        <div className="border border-border rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-[hsl(var(--brand-teal))]" />
            <h2 className="text-lg font-semibold text-foreground">Still not working?</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            If automatic sync didn't fix it, you may have a timezone configuration issue. Try these:
          </p>
          <div className="mb-4">
            <p className="text-sm font-medium text-foreground mb-2">
              For users in regions without daylight savings (Puerto Rico, parts of Arizona, etc.):
            </p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Your timezone may need to be set manually.</li>
              <li>Choose the timezone that matches your actual location.</li>
              <li>If your displayed time is wrong even with auto-sync ON, this usually means your selected timezone is incorrect.</li>
            </ul>
          </div>
          <div>
            <p className="text-sm font-medium text-foreground mb-2">For users on company computers:</p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Your IT department may have locked time/timezone settings.</li>
              <li>Contact your IT administrator.</li>
            </ul>
          </div>
        </div>

        <div className="bg-muted/50 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-5 h-5 text-[hsl(var(--brand-teal))]" />
            <h3 className="text-sm font-semibold text-foreground">Need help?</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            If you've tried the above and still can't sign in, email{' '}
            <a
              href="mailto:support@lanerunner.com"
              className="text-[hsl(var(--brand-teal))] font-medium hover:underline"
            >
              support@lanerunner.com
            </a>{' '}
            with:
          </p>
          <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside mb-3">
            <li>A screenshot of your device's Date & Time settings</li>
            <li>The country/state you're in</li>
            <li>The device type (Windows PC, Mac, iPhone, Android)</li>
          </ul>
          <p className="text-sm text-muted-foreground">We'll help you within one business day.</p>
        </div>
      </div>
    </div>
  );
};

export default ClockHelpPage;
